import { createContext, useContext, useEffect } from "react";
import { DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS } from "@verity/proctoring-option";
import { PortalAuthenticationManager } from "./portal-authentication-manager";
import { CandidatePageKey, PortalContext, PortalPageInfo, PortalPageKey, UserPortalContext } from "./portal-config";
import {
	CourseInfo,
	DEFAULT_VERITY_INSTITUTE_SETTINGS,
	GetIntegrationConfigurationParams,
	VerityInstituteSettings,
} from "@verity/components";
import type { AssessmentInfo, InstituteSettings, PlatformInfo, CourseSettings } from "@verity/api-client/types";
import { BrowserType, InstituteSetupGuide } from "@yujacorp/verity-api-contract";
import { UserInfo } from "../types/general-types";
import { UserPrivileges } from "../helpers/privilege";
import type { PortalSettingsPermission } from "./general-settings-access";
import {
	createAssessmentLaunchIntentConsumer,
	type AuthenticatedTabLaunchIntent,
} from "../proctoring/assessment-launch";

const PortalAuthenticationManagerContext = createContext({} as PortalAuthenticationManager);
export default PortalAuthenticationManagerContext;

export type PortalContextUpdate = {
	courseId?: number | null;
	assessmentId?: number | null;
};

export type PortalUserInfo = {
	id?: number;
	uuid?: string;
	userInfo?: UserInfo;
};

export type ApiIntegrationInfo = {
	course?: {
		edit: boolean;
	};
	institute?: {
		edit: boolean;
	};
};

type PortalAction = "createPracticeQuiz" | "syncLmsAccounts" | "courseScopeTest" | "instituteScopeTest";

export type PortalOAuthError = {
	name: string;
	description: string;
};

export type PortalUiElement = "introVideo" | "visualizerBanner" | "quickSetupGuide" | "setupGuideBanner";

export type SetupGuideContext = Omit<GetIntegrationConfigurationParams, "lms" | "disabledTypes"> &
	Pick<
		InstituteSetupGuide,
		| "createQuizUrl"
		| "takeQuizUrl"
		| "resultQuizUrl"
		| "setupLmsQuizId"
		| "setupLmsCourseId"
		| "setupQuizId"
		| "setupProfileId"
	>;

export interface PortalController {
	topFrame: boolean;

	initialPortalContext: PortalContext;

	userPlatformInfo: PlatformInfo;

	browserType: BrowserType;

	verityInstituteSettings: VerityInstituteSettings;

	instituteSettings: InstituteSettings;

	courseInfo?: CourseInfo;

	courseSettings: CourseSettings;

	userInfo?: PortalUserInfo;

	userPrivileges: UserPrivileges;

	portalPermissions: PortalSettingsPermission[];

	apiIntegrations: ApiIntegrationInfo;

	fetchApiIntegrations: () => Promise<void>;

	fetchInstituteSettings: () => Promise<void>;

	pages: Record<string, PortalPageInfo>;

	currPage?: PortalPageKey | CandidatePageKey;

	action?: PortalAction;

	setAction?: (action?: PortalAction) => void;

	navigateToPage: (pageKey: PortalPageKey | CandidatePageKey, state?: unknown) => void;

	portalContext: UserPortalContext;

	reportContext: UserPortalContext;

	changePortalContext: (context: PortalContextUpdate) => Promise<void>;

	returnToAssessmentInfo?: {
		url: string;
		assessmentInfo?: Partial<AssessmentInfo>;
	};

	setupGuideContext: SetupGuideContext;

	changeSetupGuideContext: (context: Partial<SetupGuideContext>) => void;

	openQuickSetupGuide: (shouldNavigate?: boolean) => void;

	canSwitchCourse: boolean;

	dismissableUiElements: Set<PortalUiElement>;

	dismissUiElement: (item: PortalUiElement) => void;

	/**
	 * Lets the routed page content set a title shown in the portal shell's top bar (e.g. the assessment
	 * name on the LMS results page). Callers should clear it (pass `undefined`) on unmount.
	 */
	pageTitle?: string;

	setPageTitle: (title?: string) => void;

	/**
	 * Lets the routed page content flag that it has unsaved changes (e.g. an unsaved settings form).
	 * Callers should clear it (pass `false`) once changes are saved/discarded or on unmount.
	 * The flag is intentionally not exposed as a context value: it is tracked outside React state so
	 * flagging changes doesn't re-render every context consumer. Use `confirmNavigation` to act on it.
	 */
	setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;

	/**
	 * Runs `action` immediately unless `hasUnsavedChanges` is true, in which case it shows an
	 * "Unsaved Changes" confirmation modal and only runs `action` if the user confirms leaving.
	 */
	confirmNavigation: (action: () => void) => void;

	consumeAssessmentLaunchIntent: (intent: AuthenticatedTabLaunchIntent) => boolean;
}

export const DEFAULT_INSTITUTE_SETTINGS: InstituteSettings = {
	...DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	suffix: null,
	managedGuestSessionRequireIncognito: true,
	proctoringRegistrationNotifications: true,
	delayRecordingUntilAssessmentStart: false,
	recordingMaxAgeMonths: 12,
	sessionRecordingLimitHours: null,
	allowPortalMadeThirdPartyAssessments: false,
	preferredProctoringSetupUiVersion: "v2",
	platformChatEnabled: false,
};

export const DEFAULT_COURSE_SETTINGS: CourseSettings = {
	geofenceLocations: [],
};

export const defaultHomeOrigin = window.location.origin;

const initialPortalContext = { homeOrigin: defaultHomeOrigin };

export const DEFAULT_SETUP_GUIDE_CONTEXT: SetupGuideContext = {
	setupProfileId: null,
	setupQuizId: null,
};

export const PortalControllerContext = createContext<PortalController>({
	topFrame: false,
	initialPortalContext,
	userPlatformInfo: { platform: "none", versionSupported: false },
	browserType: "desktop-browser",
	verityInstituteSettings: DEFAULT_VERITY_INSTITUTE_SETTINGS,
	instituteSettings: DEFAULT_INSTITUTE_SETTINGS,
	courseSettings: DEFAULT_COURSE_SETTINGS,
	userPrivileges: {},
	portalPermissions: [],
	apiIntegrations: {},
	fetchApiIntegrations: async () => {},
	fetchInstituteSettings: async () => {},
	pages: {},
	navigateToPage: () => {},
	portalContext: initialPortalContext,
	reportContext: initialPortalContext,
	changePortalContext: async () => {},
	setupGuideContext: DEFAULT_SETUP_GUIDE_CONTEXT,
	changeSetupGuideContext: () => {},
	openQuickSetupGuide: () => {},
	canSwitchCourse: false,
	dismissableUiElements: new Set(),
	dismissUiElement: () => {},
	setPageTitle: () => {},
	setHasUnsavedChanges: () => {},
	confirmNavigation: (action) => action(),
	consumeAssessmentLaunchIntent: createAssessmentLaunchIntentConsumer(),
});

export const usePortalStateContext = () => useContext(PortalControllerContext);

/**
 * Reports `hasUnsavedChanges` to the portal shell (see `setHasUnsavedChanges`) and clears the flag on
 * unmount, so in-app navigation away from the calling page shows the unsaved-changes confirmation modal.
 */
export function useReportUnsavedChanges(hasUnsavedChanges: boolean) {
	const { setHasUnsavedChanges } = useContext(PortalControllerContext);
	useEffect(() => {
		setHasUnsavedChanges(hasUnsavedChanges);
		return () => setHasUnsavedChanges(false);
	}, [setHasUnsavedChanges, hasUnsavedChanges]);
}
